import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Form, Button, Container, Header, Segment, Message } from 'semantic-ui-react';
import { useToast } from './utils/Toast';
import { useTheme } from './ThemeContext';
import { supabase } from './supabaseClient';
import './AuthPages.css';

const CreateAccount: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteToken = searchParams.get('invite');
  const { showToast } = useToast();
  const theme = useTheme();
  
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const decodedInviteEmail = useMemo(() => {
    if (!inviteToken) {
      return null;
    }

    try {
      const decoded = atob(inviteToken);
      const trimmed = decoded.trim();
      if (!trimmed || !trimmed.includes('@')) {
        return null;
      }
      return trimmed;
    } catch (error) {
      console.error('Failed to decode invite token:', error);
      return null;
    }
  }, [inviteToken]);

  useEffect(() => {
    if (!inviteToken) {
      return;
    }

    if (decodedInviteEmail) {
      setEmail(decodedInviteEmail);
      setInviteError(null);
    } else {
      setInviteError('Invitation link is invalid or expired.');
    }
  }, [inviteToken, decodedInviteEmail]);

  const logoSrc = theme === 'dark' 
    ? '/assets/Pilbara-risk-solution-logo-whitetext.svg'
    : '/assets/Pilbara-risk-solution-logo-blacktext.svg';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    if (!email) {
      showToast('Invite email is missing.', 'error');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      showToast('Passwords do not match', 'error');
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      showToast('Password must be at least 8 characters long', 'warning');
      setLoading(false);
      return;
    }

    if (!name.trim()) {
      showToast('Please enter your name', 'warning');
      setLoading(false);
      return;
    }

    try {
      const {
        error,
        data: { session }
      } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { full_name: name },
          emailRedirectTo: `${window.location.origin}/reset-password`
        }
      });

      if (error) {
        throw error;
      }

      if (!session) {
        showToast('Check your email to confirm your account.', 'success');
      } else {
        showToast('Account created successfully!', 'success');
      }

      navigate('/');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to create account right now.';
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!inviteToken || inviteError) {
    return (
      <div className="auth-container">
        <Container text>
          <div className="auth-card">
            <div className="auth-logo-container">
              <img src={logoSrc} alt="Pilbara Risk Solutions" className="auth-logo" />
            </div>
            
            <Segment raised className="auth-segment">
              <Message warning>
                <Message.Header>Invalid Invitation</Message.Header>
                <p>{inviteError || 'Invalid invitation link.'}</p>
              </Message>

              <div className="auth-links">
                <Link to="/" className="auth-link">
                  Back to Sign In
                </Link>
              </div>
            </Segment>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <Container text>
        <div className="auth-card">
          <div className="auth-logo-container">
            <img src={logoSrc} alt="Pilbara Risk Solutions" className="auth-logo" />
          </div>
          
          <Header as="h2" textAlign="center" className="auth-subheader">
            Create Your Account
          </Header>
          
          <Segment raised className="auth-segment">
            <Form onSubmit={handleSubmit}>
              <Form.Field>
                <label>Email</label>
                <Form.Input
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  icon="mail"
                  iconPosition="left"
                  readOnly={Boolean(decodedInviteEmail)}
                />
              </Form.Field>

              <Form.Field>
                <label>Full Name</label>
                <Form.Input
                  type="text"
                  placeholder="Enter your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  icon="user"
                  iconPosition="left"
                />
              </Form.Field>

              <Form.Field>
                <label>Password</label>
                <Form.Input
                  type="password"
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  icon="lock"
                  iconPosition="left"
                />
                <small className="field-hint">Must be at least 8 characters</small>
              </Form.Field>

              <Form.Field>
                <label>Confirm Password</label>
                <Form.Input
                  type="password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  icon="lock"
                  iconPosition="left"
                />
              </Form.Field>

              <Button
                type="submit"
                primary
                fluid
                size="large"
                loading={loading}
                disabled={loading}
                className="auth-button"
              >
                Create Account
              </Button>
            </Form>

            <div className="auth-links">
              <Link to="/" className="auth-link">
                Already have an account? Sign In
              </Link>
            </div>
          </Segment>
        </div>
      </Container>
    </div>
  );
};

export default CreateAccount;
